import React, { useState, useEffect } from 'react';
import { usePolywizzContext } from '../context';
import { Link } from 'react-router-dom';
import DateColumn from '../../../components/DateColumn';
import { InsurancePolywizzStatuses, Polywizz } from '../../../interfaces/Insurance';
import { Tooltip } from '@mui/material';
import Spinner from 'react-bootstrap/Spinner';
import { Modal } from 'react-bootstrap';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import globalErrorHandler from '../../../utils/globalErrorHandler';
import { resubmitPolywizzPolicies, submitPolywizz, getPolywizzStatusTrackers } from '../../../utils/api/insurance';
import TranslatedColumn from '../../../components/TranslatedColumn';
import i18next from 'i18next';
import { Client } from '../../../interfaces/Client';
import { formatDate } from '../../../utils/formatDate';

const ErrorColumn = ({ value }: { value: string }) => {
    return <div className="error">{value}</div>;
};

interface PolywizzStatusTracker {
    _id: string;
    polywizz: string;
    client: string;
    newStatus: string;
    prevStatus?: string;
    createdAt: Date;
}

const StatusColumn = (params: any) => {
    const polywizz: Polywizz = params.row.original;
    const [showModal, setShowModal] = useState(false);
    const [statusTrackers, setStatusTrackers] = useState<PolywizzStatusTracker[]>([]);
    const [loading, setLoading] = useState(false);

    const handleOpenModal = async () => {
        setShowModal(true);
        setLoading(true);
        try {
            const response = await getPolywizzStatusTrackers({ polywizzId: polywizz._id });
            setStatusTrackers(response?.statusTrackers || []);
        } catch (error) {
            console.error('Error fetching polywizz status trackers:', error);
            setStatusTrackers([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div>
                <div>{polywizz.status}</div>
                <a
                    className="text-primary cursor-pointer"
                    style={{ fontSize: '12px' }}
                    onClick={handleOpenModal}
                >
                    <i className="mdi mdi-history me-1"></i>
                    History
                </a>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Polywizz Status History - {(polywizz.client as Client)?.fullName || ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" variant="secondary" />
                        </div>
                    ) : statusTrackers.length > 0 ? (
                        <table className="table table-striped table-bordered" style={{ fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Time of Change</th>
                                </tr>
                            </thead>
                            <tbody>
                                {statusTrackers.map((tracker, index) => (
                                    <tr key={tracker._id || index}>
                                        <td>{camelCaseToText(tracker.newStatus || '')}</td>
                                        <td>{formatDate(tracker.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-muted">No status history available.</div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

const RowIndexingColumn = (params: any) => {
    const { filterConfigs } = usePolywizzContext();
    const { page, MAX_ITEMS_PER_PAGE } = filterConfigs;
    const skip = page * MAX_ITEMS_PER_PAGE;
    return <>{skip + params.row.index + 1}</>;
};

const getPoliciesStatuses = [
    InsurancePolywizzStatuses.timeout,
    InsurancePolywizzStatuses.completedPoliciesNotRequired,
    InsurancePolywizzStatuses.mountainDataReceived,
    InsurancePolywizzStatuses.policiesError,
];

const resubmitMountainStatuses = [
    InsurancePolywizzStatuses.pending,
    InsurancePolywizzStatuses.failed,
    InsurancePolywizzStatuses.temporaryError,
    InsurancePolywizzStatuses.permanentError,
];

const ActionsColumn = (params: any) => {
    const polywizz: Polywizz = params.row.original;
    const [isPolywizzResubmitLoading, setIsPolywizzResubmitLoading] = useState(false);
    const [isPolywizzResubmitSuccess, setIsPolywizzResubmitSuccess] = useState(false);

    const handleResubmitPolywizz = async (polywizz: Polywizz, familyMemberType: string) => {
        try {
            setIsPolywizzResubmitLoading(true);
            if (getPoliciesStatuses.includes(polywizz.status)) {
                await resubmitPolywizzPolicies({ polywizzId: polywizz._id });
            } else {
                await submitPolywizz({ polywizzId: polywizz._id });
            }
            setIsPolywizzResubmitSuccess(true);
        } catch (err) {
            globalErrorHandler(err);
        } finally {
            setIsPolywizzResubmitLoading(false);
        }
    };

    if (!getPoliciesStatuses.concat(resubmitMountainStatuses).includes(polywizz.status)) {
        return <></>;
    }

    return (
        <span className="d-inline-flex align-items-center gap-1 cursor-pointer">
            <a onClick={() => handleResubmitPolywizz(polywizz, polywizz.familyMemberType)}>
                {getPoliciesStatuses.includes(polywizz.status) && <span>Get policies</span>}
                {resubmitMountainStatuses.includes(polywizz.status) && <span>Resubmit Polywizz</span>}
            </a>

            {isPolywizzResubmitLoading ? (
                <Spinner animation="border" variant="secondary" size="sm" />
            ) : isPolywizzResubmitSuccess ? (
                <i className="fas fa-check-circle text-success"></i>
            ) : (
                <i
                    className="mdi mdi-reload text-secondary cursor-pointer"
                    onClick={() => handleResubmitPolywizz(polywizz, polywizz.familyMemberType)}
                ></i>
            )}
        </span>
    );
};

const FamilyMemberColumn = (params: any) => {
    const client = params.row.original.client || params.row.original.policy?.client || params.row.original.product?.client;

    if (!client) return null;
    return (
        <>
            <>
                <span>
                    <span className="client-name" onClick={() => window.open(`/clients/profile?clientId=${client._id}`, '_blank')}>
                        <b>{client.fullName}</b>
                    </span>
                    <br />
                    <span>{client.idNumber}</span>
                </span>
                <span className="client-name" onClick={() => window.open(`/product-transfers?clientId=${client._id}`, '_blank')}>
                    {'   '}
                    <i className="mdi mdi-filter me-1" />
                </span>
            </>
        </>
    );
};

const ClientColumn = (params: any) => {
    const polywizz: Polywizz = params.row.original;
    const client: Client = polywizz.client as Client;
    if (!client) return <></>;

    if (polywizz.familyMemberType === 'client') {
        return (
            <>
                <span>
                    <span className="client-name link" onClick={() => window.open(`/clients/profile?clientId=${client._id}`, '_blank')}>
                        <b>{client.fullName}</b>
                    </span>
                    <br />
                    <span>{client.idNumber}</span>
                </span>
                <Link to={`/insurance-polywizz?clientIds=${client._id}`}>
                    {' '}
                    <i className="mdi mdi-filter me-1" />
                </Link>
            </>
        );
    }

    if (client.spouse?.client) {
        return (
            <>
                <span>
                    <span className="client-name link" onClick={() => window.open(`/clients/profile?clientId=${client.spouse?.client}`, '_blank')}>
                        <b>
                            {client.spouse?.firstName} {client.spouse?.lastName}
                        </b>
                    </span>
                    <br />
                    <span>{client.spouse?.idNumber}</span>
                </span>
                <Link to={`/insurance-polywizz?clientIds=${client.spouse?.client}`}>
                    {' '}
                    <i className="mdi mdi-filter me-1" />
                </Link>
            </>
        );
    }
    return (
        <>
            <span>
                <span className="client-name">
                    <b>
                        {client.spouse?.firstName} {client.spouse?.lastName}
                    </b>
                </span>
                <br />
                <span>{client.spouse?.idNumber}</span>
            </span>
        </>
    );
};

export const columns = [
    {
        Header: '#',
        accessor: 'index',
        Cell: RowIndexingColumn,
        headerClassName: 'p-2',
        sort: false,
    },
    {
        Header: 'Client',
        accessor: 'client.fullName',
        sort: true,
        classes: 'table-productTransfer',
        Cell: ClientColumn,
    },
    {
        Header: 'Family Member',
        accessor: 'familyMemberType',
        headerClassName: 'p-2',
        sort: false,
    },
    {
        Header: 'Status',
        accessor: 'status',
        headerClassName: 'p-2',
        sort: false,
        Cell: StatusColumn,
    },
    // _id: string;
    // client: string | Client;

    // clientId: string;
    // taskId: string;

    {
        Header: 'Submission Time',
        accessor: 'submissionTime',
        Cell: DateColumn,
        sort: true,
    },
    {
        Header: 'Policies Submission Time',
        accessor: 'policiesSubmissionTime',
        Cell: DateColumn,
        sort: true,
    },
    {
        Header: 'Error',
        accessor: 'error',
        headerClassName: 'p-2',
        Cell: ErrorColumn,
        sort: false,
    },
    {
        Header: 'Actions',
        accessor: 'updatedAt',
        Cell: ActionsColumn,
    },
];
